import { Injectable } from '@angular/core';
import { Co6TransactionTypes } from '@interfaces';
import { SubmitDocumentsResponse } from '@interfaces/models/doc-types';
import { EmailTemplatesEnum } from '@interfaces/models/email-templates';
import { IndividualDesignationListedTypes } from '@interfaces/models/individual-designation-types';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { resolveDraftIdsToDelete } from '@shared/helpers';
import { removeRestrictedHoldings } from '@shared/helpers/restricted-holdings';
import { sanitizeError } from '@shared/helpers/sanitize-error-helper';
import { flowMapper } from '@shared/models';
import { LoggerCodes, LoggerService } from '@shared/modules/logger/logger';
import { WindowRef } from '@shared/modules/window-ref/window-ref';
import {
  DocumentsService,
  EmailService,
  FMFService,
  selectOAFromStore,
  SubmitService,
} from '@shared/services';
import { mergeFilesToPdf } from '@src/app/secure/document-upload/helpers/merge-files-to-pdf.helper';
import {
  selectTestMonkeyState,
  testMonkeyNextScenario,
  testMonkeyResetState,
  testMonkeyUpdateResponses,
} from '@src/app/test-monkey/store';
import {
  appianSubmit,
  convertRegistration,
  internalTransferAppianSubmit,
  submitCompleted,
  submitFailed,
  submitInProgress,
  submitMetricsMultiFlowSubmit,
  updateIsStp,
} from '@store/actions';
import { DeleteDrafts } from '@store/actions/draft/draft.actions';
import { MfePersonalInfoClientState } from '@store/reducers/mfe-personal-info-client/mfe-personal-info-client.reducer';
import { getClientName } from '@store/reducers/personal-data/personal-data.reducer';
import {
  getIsDivorceFlow,
  getIsGiftingFlow,
  getIsInternalTransferFlow,
  getIsIRASingleExecutor,
  getIsMinorFlow,
  TriageState,
} from '@store/reducers/triage/triage.reducer';
import {
  selectCO0UploadToggle,
  selectIsDivorceFlow,
  selectIsInternalTransferFlow,
  selectIsMultiInheritanceFlow,
  selectIsOpeningNewAccountAutomatically,
  selectIsOrgToOrgTransfer,
  selectIsProspect,
  selectTaxFilesMetadata,
  selectTriageState,
} from '@store/selectors';
import { selectDraftId, selectDrafts } from '@store/selectors/draft/draft.selectors';
import { selectFraudDetectorSimulatorLevel } from '@store/selectors/fraud-detector-simulator/fraud-detector-simulator.selectors';
import { selectLetterFilesMetadata } from '@store/selectors/letter-upload/letter-upload.selectors';
import { selectMfePersonalInfoClientPersonalInfo } from '@store/selectors/mfe-personal-info-client/mfe-personal-info-client.selectors';
import { selectSubmitMetricsID } from '@store/selectors/open-account/open-account.selectors';
import { selectAcceptedDAOffer } from '@store/selectors/open-choice/open-choice.selectors';
import {
  catchError,
  filter,
  forkJoin,
  from,
  Observable,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { selectRecaptchaMockResult } from 'vg-interaction-model';

import { getFileDescriptorsFromMetadata } from '../../../../secure/document-upload/helpers';
import {
  closeSpinner,
  enrollInDA,
  Go,
  LogError,
  LogInfo,
  Next,
  submitMetricsFlowSubmit,
  TrackProcessComplete,
  TrackProcessError,
  TrackSendDocuments,
  UpdateAccountId,
} from '../../actions';
import * as fromRootStore from '../../index';
import { getOpenAccountState } from '../../reducers';
import { IFileMetaData } from '../../reducers/document-upload/document-upload.reducer';
import {
  selectFilesMetadata,
  selectIsWldDocUploadEnabled,
  selectWldDocuments,
} from '../../selectors/document-upload/document-upload.selectors';
import { selectClientEmailAddress } from '../../selectors/personal-data/personal-data.selectors';
import {
  getITSubmitPayload,
  getMultiSubmitPayload,
  getSubmitPayload,
  selectIsSubmitInProgress,
} from '../../selectors/submit/submit.selectors';

@Injectable()
export class SubmitEffects {
  constructor(
    private actions$: Actions,
    private submitService: SubmitService,
    private store$: Store<fromRootStore.State>,
    private loggerService: LoggerService,
    private windowRef: WindowRef,
    private emailService: EmailService,
    private fmfService: FMFService,
    private documentsService: DocumentsService,
  ) {}

  submit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appianSubmit),
      withLatestFrom(
        this.store$.pipe(select(selectIsSubmitInProgress)),
        this.store$.pipe(select(getSubmitPayload)),
        this.store$.pipe(select(selectFilesMetadata)),
        this.store$.pipe(select(selectLetterFilesMetadata)),
        this.store$.pipe(select(selectTestMonkeyState)),
        this.store$.pipe(select(selectRecaptchaMockResult)),
        this.store$.pipe(select(selectAcceptedDAOffer)),
        this.store$.pipe(select(fromRootStore.selectIsCO0Flow)),
        this.store$.pipe(select(selectCO0UploadToggle)),
        this.store$.pipe(select(selectOAFromStore)),
        this.store$.pipe(select(selectIsOpeningNewAccountAutomatically)),
        this.store$.pipe(select(selectFraudDetectorSimulatorLevel)),
        this.store$.pipe(select(getMultiSubmitPayload)),
        this.store$.pipe(select(selectIsMultiInheritanceFlow)),
        this.store$.pipe(select(selectTaxFilesMetadata)),
        this.store$.pipe(select(selectSubmitMetricsID)),
        this.store$.pipe(select(selectIsWldDocUploadEnabled)),
        this.store$.pipe(select(selectWldDocuments)),
        this.store$.pipe(select(fromRootStore.selectIsTrustToTrustFlow)),
        this.store$.pipe(select(fromRootStore.selectCwpInheritanceSaveStateToggle)),
      ),
      filter(([, isSubmitInProgress, , , , , , , , , , , , ,]) => {
        return !isSubmitInProgress;
      }),
      tap(() => {
        this.store$.dispatch(submitInProgress());
      }),
      switchMap(
        ([
          ,
          ,
          submitPayload,
          filesMetadata,
          letterFilesMetadata,
          testMonkeyState,
          mockRecaptchaLevel,
          acceptedDAOffer,
          isCO0Flow,
          isCO0UploadToggle,
          oaFromStore,
          isAccountOpeningAutomatic,
          mockFraudDetectorLevel,
          multiSubmitPayloadResult,
          isMultiInheritanceFlow,
          taxFilesMetadata,
          submitMetricsCorrelationId,
          isWldDocUploadEnabled,
          wldDocuments,
          isTrustToTrustFlow,
          isSaveStateEnabled,
        ]) => {
          const shouldCheckBlobValidity =
            isSaveStateEnabled && (isMultiInheritanceFlow || isTrustToTrustFlow);
          let documentSubmit$: Observable<SubmitDocumentsResponse | null | undefined>;
          if (isWldDocUploadEnabled) {
            this.loggerService.info(
              {
                eventId: 'submit-wld-documents',
                message: `Submitting WLD documents: count=${wldDocuments.length}, documentIds=[${wldDocuments.map((d) => d.documentId).join(',')}]`,
              },
              LoggerCodes.GENERAL_SUCCESS,
            );
            documentSubmit$ = from(this.documentsService.submitWldDocuments(wldDocuments));
          } else if (isCO0UploadToggle || isMultiInheritanceFlow) {
            documentSubmit$ = from(
              this.documentsService.submitDocuments(
                [...filesMetadata, ...letterFilesMetadata, ...taxFilesMetadata],
                shouldCheckBlobValidity,
              ),
            );
          } else {
            documentSubmit$ = of(null);
          }

          return forkJoin([
            of(submitPayload),
            this.convertToBase64PDF(
              [...filesMetadata, ...letterFilesMetadata, ...taxFilesMetadata],
              shouldCheckBlobValidity,
            ),
            of(testMonkeyState),
            this.fmfService.getAllTokens({ recaptcha: 'COO_APPIAN_SUBMIT' }),
            isCO0Flow && isAccountOpeningAutomatic
              ? this.fmfService.getAllTokens({ recaptcha: 'COO_OA_SUBMIT' })
              : of(null),
            of(mockRecaptchaLevel),
            of(acceptedDAOffer),
            of(isCO0Flow),
            of(isCO0UploadToggle),
            documentSubmit$,
            of(oaFromStore),
            isCO0Flow && isAccountOpeningAutomatic ? of(mockFraudDetectorLevel) : of(null),
            of(multiSubmitPayloadResult),
            of(isMultiInheritanceFlow),
            of(submitMetricsCorrelationId),
            of(isWldDocUploadEnabled),
          ]);
        },
      ),
      switchMap(
        ([
          submitPayload,
          base64Pdf,
          testMonkeyState,
          fmfResponse,
          oaFMFresponse,
          mockRecaptchaLevel,
          acceptedDAOffer,
          isCO0Flow,
          isCO0UploadToggle,
          documentFileName,
          oaFromStore,
          mockFraudDetectorLevel,
          multiSubmitPayloadResult,
          isMultiInheritanceFlow,
          submitMetricsCorrelationId,
          isWldDocUploadEnabled,
        ]) => {
          const multiSubmitPayload = multiSubmitPayloadResult?.payloads ?? null;

          if (multiSubmitPayloadResult?.error) {
            this.loggerService.error(
              {
                eventId: 'createMultiSubmitPayload-error',
                message: multiSubmitPayloadResult.error,
              },
              LoggerCodes.SUBMIT_ERROR,
            );
          }

          if (isMultiInheritanceFlow && !multiSubmitPayload) {
            this.loggerService.error(
              {
                eventId: 'createMultiSubmitPayload-null',
                message: `multiSubmitPayload is null for MULTI_INHERITANCE flow. Error: ${multiSubmitPayloadResult?.error ?? 'no error - returned null intentionally'}`,
              },
              LoggerCodes.SUBMIT_ERROR,
            );
          }

          const payloads = multiSubmitPayload || [submitPayload];

          if (!isCO0UploadToggle && !isMultiInheritanceFlow && !isWldDocUploadEnabled) {
            payloads.forEach((payload) => {
              payload.config.base64Pdf = base64Pdf;
            });
          }
          this.loggerService.info(
            { eventId: 'createEffects-status', message: 'Submit Payload in-Progress' },
            LoggerCodes.GENERAL_SUCCESS,
          );

          if (
            (isCO0UploadToggle || isMultiInheritanceFlow || isWldDocUploadEnabled) &&
            documentFileName?.fileName
          ) {
            const dotIndex = documentFileName.fileName.lastIndexOf('.');
            const filename =
              dotIndex > 0
                ? documentFileName.fileName.substring(0, dotIndex)
                : documentFileName.fileName;
            const fileExtension =
              dotIndex > 0 ? documentFileName.fileName.substring(dotIndex + 1).toLowerCase() : '';
            payloads.forEach((payload) => {
              payload.config.caseInformationFiles = [
                {
                  filename,
                  fileExtension,
                },
              ];
            });
          }

          const isInheritedIraFlow = submitPayload.transactionType === 'INHERITED_IRA';

          let submitEndpoint: Observable<any>;

          if (isInheritedIraFlow) {
            submitEndpoint = this.submitService.submitInheritedIraEndpoint(
              submitPayload,
              fmfResponse,
              mockRecaptchaLevel,
            );
          } else if (isCO0Flow) {
            submitEndpoint = this.submitService.submitMultiTransactionsEndpoint(
              payloads,
              fmfResponse,
              oaFMFresponse,
              mockRecaptchaLevel,
              oaFromStore,
              mockFraudDetectorLevel,
              submitMetricsCorrelationId,
            );
          } else {
            submitEndpoint = this.submitService.submitEndpoint(
              submitPayload,
              fmfResponse,
              mockRecaptchaLevel,
            );
          }

          return submitEndpoint.pipe(
            switchMap((res) => {
              const accountId: string =
                submitPayload.config.selectedAccs.transferTo?.selectedAccount?.accountData
                  .accountId;

              const isMinorFlow: boolean = submitPayload.transactionType === 'MINOR';
              if (isCO0Flow && res.correlationId === '') {
                throw new Error(`Error calling CO0`);
              }
              try {
                const actions = [];
                const isStp = 'transactId' in res;
                const transactionId =
                  res.appianSuccess?.bpmCaseId?.toString().replace(/,/g, '|') ||
                  res.transactId ||
                  res.correlationId ||
                  '';

                if (multiSubmitPayload) {
                  actions.push(
                    LogInfo({
                      payload: {
                        eventId: 'submitEffects-status',
                        message: 'Submit Payload Success',
                      },
                    }),
                  );

                  if (multiSubmitPayload[0].config.base64Pdf) {
                    actions.push(TrackSendDocuments());
                  }

                  actions.push(
                    TrackProcessComplete({
                      processType: `coo-${flowMapper[multiSubmitPayload[0].config.triage.flowType]}-flow`,
                      accountType: multiSubmitPayload[0].config.triage.accountTypeInheriting,
                      transactionId,
                    }),
                  );

                  actions.push(submitMetricsMultiFlowSubmit());
                  actions.push(submitCompleted());
                  actions.push(updateIsStp({ payload: isStp }));
                  return actions;
                }

                if (isMinorFlow && acceptedDAOffer) {
                  actions.push(UpdateAccountId({ payload: accountId }));
                  actions.push(enrollInDA());
                }
                actions.push(
                  LogInfo({
                    payload: {
                      eventId: 'submitEffects-status',
                      message: 'Submit Payload Success',
                    },
                  }),
                );
                if (submitPayload.config.base64Pdf) actions.push(TrackSendDocuments());
                actions.push(
                  TrackProcessComplete({
                    processType: `coo-${flowMapper[submitPayload.config.triage.flowType]}-flow`,
                    accountType: submitPayload.config.triage.accountTypeInheriting,
                    transactionId,
                  }),
                );

                if (testMonkeyState?.monkeyMood) {
                  const monkeyTable = [res.appianSuccess?.bpmCaseId, res.success];
                  actions.push(testMonkeyUpdateResponses({ payload: monkeyTable }));
                }

                actions.push(submitMetricsFlowSubmit({ payload: transactionId }));
                actions.push(submitCompleted());
                actions.push(updateIsStp({ payload: isStp }));
                return actions;
              } catch (error) {
                return [
                  LogError({
                    payload: {
                      eventId: 'submitEndpoint-response-error',
                      message: `Submit Response Error: ${JSON.stringify(sanitizeError(error))}`,
                      code: LoggerCodes.NULL_FAILURE,
                    },
                  }),
                  submitCompleted(),
                ];
              }
            }),
            catchError((e) => {
              return [
                submitFailed(),
                closeSpinner(),
                LogError({
                  payload: {
                    eventId: 'error-page-catch-submitService',
                    message: `Submit Service Error: ${JSON.stringify(sanitizeError(e))}`,
                    code: LoggerCodes.NULL_FAILURE,
                  },
                }),
              ];
            }),
          );
        },
      ),
      catchError((e) => {
        return [
          submitFailed(),
          closeSpinner(),
          LogError({
            payload: {
              eventId: 'error-page-catch-convertPDF',
              message: `Convert PDF Error: ${JSON.stringify(sanitizeError(e))}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        ];
      }),
    ),
  );

  internalTransferSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(internalTransferAppianSubmit),
      withLatestFrom(
        this.store$.pipe(select(selectIsSubmitInProgress)),
        this.store$.pipe(select(getITSubmitPayload)),
        this.store$.pipe(select(selectRecaptchaMockResult)),
        this.store$.pipe(select(selectFilesMetadata)),
        this.store$.pipe(select(selectLetterFilesMetadata)),
        this.store$.pipe(select(selectCO0UploadToggle)),
        this.store$.pipe(select(selectIsDivorceFlow)),
        this.store$.pipe(select(selectIsInternalTransferFlow)),
        this.store$.pipe(select(selectIsOrgToOrgTransfer)),
        this.store$.pipe(select(selectIsWldDocUploadEnabled)),
        this.store$.pipe(select(selectWldDocuments)),
      ),
      filter(([, isSubmitInProgress]) => {
        return !isSubmitInProgress;
      }),
      tap(() => {
        this.store$.dispatch(submitInProgress());
      }),
      switchMap(
        ([
          ,
          ,
          submitPayload,
          mockRecaptchaLevel,
          filesMetaData,
          letterFilesMetadata,
          isCO0UploadToggle,
          isDivorceFlow,
          isInternalTransferFlow,
          isOrgToOrgTransfer,
          isWldDocUploadEnabled,
          wldDocuments,
        ]) => {
          let documentSubmit$: Observable<SubmitDocumentsResponse | null | undefined>;
          if (isWldDocUploadEnabled) {
            this.loggerService.info(
              {
                eventId: 'submit-wld-documents',
                message: `Submitting WLD documents: count=${wldDocuments.length}, documentIds=[${wldDocuments.map((d) => d.documentId).join(',')}]`,
              },
              LoggerCodes.GENERAL_SUCCESS,
            );
            documentSubmit$ = from(this.documentsService.submitWldDocuments(wldDocuments));
          } else if (isCO0UploadToggle && (isDivorceFlow || isInternalTransferFlow)) {
            documentSubmit$ = from(
              this.documentsService.submitDocuments([...filesMetaData, ...letterFilesMetadata]),
            );
          } else {
            documentSubmit$ = of(null);
          }

          return forkJoin([
            of(removeRestrictedHoldings(submitPayload)),
            this.fmfService.getAllTokens({ recaptcha: 'COO_APPIAN_SUBMIT' }),
            of(mockRecaptchaLevel),
            this.convertToBase64PDF([...filesMetaData, ...letterFilesMetadata]),
            documentSubmit$,
            of(isCO0UploadToggle),
            of(isDivorceFlow),
            of(isInternalTransferFlow),
            of(isOrgToOrgTransfer),
            of(isWldDocUploadEnabled),
          ]);
        },
      ),
      switchMap(
        ([
          submitPayload,
          fmfResponse,

          mockRecaptchaLevel,
          base64Pdf,
          documentFileName,
          isCO0UploadToggle,
          isDivorceFlow,
          isInternalTransferFlow,
          isOrgToOrgTransfer,
          isWldDocUploadEnabled,
        ]) => {
          const transferFromTriage = submitPayload.itConfig.internalTransfer.triage.transferFrom;
          const transferToTriage = submitPayload.itConfig.internalTransfer.triage.transferTo;
          const isVanguardCharitable =
            submitPayload.transactionType === Co6TransactionTypes.vanguardCharitable;

          this.loggerService.info(
            {
              eventId: 'submit-effects-status',
              message: `Internal Transfer Submit in Progress for: ${submitPayload.itConfig.internalTransfer.transferFrom.totalTransferValue}`,
              data: {
                transactionType: submitPayload.transactionType,
                isVanguardCharitable,
                isOrgToOrgTransfer,
                transferFrom: transferFromTriage,
                transferTo: transferToTriage,
              },
            },
            LoggerCodes.GENERAL_SUCCESS,
          );

          if (
            (isDivorceFlow || isInternalTransferFlow) &&
            !isWldDocUploadEnabled &&
            (!isCO0UploadToggle || !documentFileName?.fileName)
          ) {
            submitPayload.itConfig.base64Pdf = base64Pdf;
          }
          if (
            (isCO0UploadToggle || isWldDocUploadEnabled) &&
            (isDivorceFlow || isInternalTransferFlow) &&
            documentFileName?.fileName
          ) {
            const dotIndex = documentFileName.fileName.lastIndexOf('.');
            const filename =
              dotIndex > 0
                ? documentFileName.fileName.substring(0, dotIndex)
                : documentFileName.fileName;
            const fileExtension =
              dotIndex > 0 ? documentFileName.fileName.substring(dotIndex + 1).toLowerCase() : '';
            submitPayload.itConfig.caseInformationFiles = [
              {
                filename,
                fileExtension,
              },
            ];
          }

          const submitEndpoint = this.submitService.internalTransferSubmitEndpoint(
            submitPayload,
            fmfResponse,
            mockRecaptchaLevel,
          );

          return submitEndpoint.pipe(
            switchMap((res) => {
              const isTransactIDTypeSTP = 'transactId' in res;
              const isTransactIDTypeCWP =
                'appianSuccess' in res && 'bpmCaseId' in res.appianSuccess;

              let transactionId: string = '';
              if (isTransactIDTypeSTP) {
                transactionId = res.transactId;
              } else if (isTransactIDTypeCWP) {
                transactionId = (res.appianSuccess?.bpmCaseId ?? [])
                  .map((item: string) => item.replace(/,/g, '|'))
                  .join(',');
              }

              const actions = [];
              actions.push(
                LogInfo({
                  payload: {
                    eventId: 'submitEffects-status',
                    message: 'Internal Transfer Submit Payload Success',
                    data: {
                      transactionType: submitPayload.transactionType,
                      isVanguardCharitable,
                      isOrgToOrgTransfer,
                      transferFrom: transferFromTriage,
                      transferTo: transferToTriage,
                      transactionId,
                    },
                  },
                }),
              );
              actions.push(
                TrackProcessComplete({
                  processType: `coo-internal_transfer-flow`,
                  accountType: `${transferFromTriage}|${transferToTriage}`,
                  transactionId,
                  ...(isOrgToOrgTransfer ? { isOrgTransfer: true } : {}),
                }),
              );
              actions.push(submitMetricsFlowSubmit({ payload: transactionId }));
              actions.push(submitCompleted());
              actions.push(updateIsStp({ payload: isTransactIDTypeSTP }));

              return actions;
            }),
            catchError((e) => {
              return [
                submitFailed(),
                closeSpinner(),
                LogError({
                  payload: {
                    eventId: 'error-page-catch-internal-transfer-submitService',
                    message: `Submit Service Error:${JSON.stringify(sanitizeError(e))}`,
                    code: LoggerCodes.NULL_FAILURE,
                    data: {
                      transactionType: submitPayload.transactionType,
                      isVanguardCharitable,
                      isOrgToOrgTransfer,
                      transferFrom: transferFromTriage,
                      transferTo: transferToTriage,
                    },
                  },
                }),
              ];
            }),
          );
        },
      ),
      catchError((e) => {
        return [
          submitFailed(),
          closeSpinner(),
          LogError({
            payload: {
              eventId: 'error-page-catch-internal-transfer-submit-effects',
              message: `Submit Effects Error: ${JSON.stringify(sanitizeError(e))}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        ];
      }),
    ),
  );

  async convertToBase64PDF(
    filesMetaData: IFileMetaData[],
    shouldRehydrate = false,
  ): Promise<string> {
    try {
      if (filesMetaData.length > 0) {
        const fileDescriptors = shouldRehydrate
          ? getFileDescriptorsFromMetadata(filesMetaData)
          : [];
        const files = fileDescriptors.length
          ? await this.documentsService.rehydrateFiles(filesMetaData, fileDescriptors)
          : filesMetaData;

        const pdfDoc = await mergeFilesToPdf({
          files,
          fetchFn: (url: string) => this.windowRef.nativeWindow.fetch(url),
          onConversion: (eventId: string, message: string) =>
            this.loggerService.info({ eventId, message }, LoggerCodes.GENERAL_SUCCESS),
        });

        const pdfBase64 = await pdfDoc.saveAsBase64();

        this.loggerService.info(
          { eventId: 'submitEffects-Success', message: 'PDF Conversion Success' },
          LoggerCodes.GENERAL_SUCCESS,
        );
        return pdfBase64;
      } else {
        this.loggerService.info(
          { eventId: 'convertToBase64PDF-status', message: 'PDF Conversion Skipped' },
          LoggerCodes.GENERAL_SUCCESS,
        );
        return '';
      }
    } catch (error) {
      this.loggerService.error(
        { eventId: 'convertToBase64PDF-error', message: String(error) },
        LoggerCodes.CONVERT_TO_PDF_FAILURE,
      );
      return '';
    }
  }

  sendEmail(
    prospect: boolean,
    triageState: TriageState,
    personalInfo: MfePersonalInfoClientState['personalInfo'],
    clientEmailAddress: string,
  ): void {
    const isExecutor =
      triageState.individualDesignation === IndividualDesignationListedTypes.EXECUTOR;
    const isMinor = getIsMinorFlow(triageState);
    const isDivorce = getIsDivorceFlow(triageState);
    const isInternalTransfer = getIsInternalTransferFlow(triageState);
    const isGifting = getIsGiftingFlow(triageState);
    const clientName = getClientName(personalInfo);

    const emailAddress = prospect ? personalInfo.email : clientEmailAddress;

    let template;
    switch (true) {
      case isMinor:
        template = EmailTemplatesEnum.COO_UGMA_UTMA;
        break;
      case isGifting:
        template = EmailTemplatesEnum.COO_GIFTING_CONFIRM;
        break;
      case isExecutor:
        template = EmailTemplatesEnum.COO_EXECUTOR_CONFIRM;
        break;
      case isDivorce:
      case isInternalTransfer:
        template = EmailTemplatesEnum.COO_INTERNAL_TRANSFER_CONFIRM;
        break;
      default:
        template = EmailTemplatesEnum.COO_BENEFICIARY_CONFIRM;
        break;
    }

    this.emailService
      .sendEmail({
        toAddress: emailAddress,
        template,
        clientName,
      })
      .subscribe();
  }

  submitCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitCompleted),
      withLatestFrom(
        this.store$.pipe(select(selectIsProspect)),
        this.store$.pipe(select(getOpenAccountState)),
        this.store$.pipe(select(selectTriageState)),
        this.store$.pipe(select(selectMfePersonalInfoClientPersonalInfo)),
        this.store$.pipe(select(selectClientEmailAddress)),
        this.store$.pipe(select(selectTestMonkeyState)),
        this.store$.pipe(select(fromRootStore.selectIsCO0Flow)),
        this.store$.pipe(select(selectDraftId)),
        this.store$.pipe(select(selectDrafts)),
      ),
      switchMap(
        ([
          ,
          prospect,
          openAccountState,
          triageState,
          personalInfo,
          clientEmailAddress,
          testMonkey,
          isCO0Flow,
          draftId,
          drafts,
        ]) => {
          const isIRAExecutor = getIsIRASingleExecutor(triageState);

          if (!isIRAExecutor && !isCO0Flow) {
            this.sendEmail(prospect, triageState, personalInfo, clientEmailAddress);
          }

          const ids = resolveDraftIdsToDelete(drafts, draftId);

          if (testMonkey?.monkeyMood) {
            return ids.length > 0
              ? [
                  testMonkeyResetState(),
                  testMonkeyNextScenario(),
                  DeleteDrafts({ ids, skipRetrieve: true }),
                ]
              : [testMonkeyResetState(), testMonkeyNextScenario()];
          } else {
            const navigationActions =
              prospect &&
              openAccountState.openAccountResponse &&
              openAccountState.openAccountResponse.success &&
              !openAccountState.openAccountResponse.isS24Reject
                ? [convertRegistration()]
                : [Next()];
            return ids.length > 0
              ? [...navigationActions, DeleteDrafts({ ids, skipRetrieve: true })]
              : navigationActions;
          }
        },
      ),
      catchError((error) => {
        this.loggerService.error(
          {
            message: `Failed to retrieve open account response or isProspect flag with error: ${error}`,
          },
          LoggerCodes.NULL_FAILURE,
        );
        return [Next()];
      }),
    ),
  );

  submitError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitFailed),
      switchMap(() => {
        return [
          Go({ path: ['error'] }),
          closeSpinner(),
          LogError({
            payload: {
              eventId: 'error-page-route-submit',
              message: 'CWP Submit Failure',
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
          TrackProcessError({
            errorCode: 'coo_appian_error',
          }),
        ];
      }),
    ),
  );
}
